import { splitMediaActions, type SplitMediaActionsOptions } from '@lib/media/mediaActionBar';
import type {
  MediaActionControl,
  MediaMenuAction,
  MediaMenuHostItem,
} from '@lib/media/mediaActions';
import { useFrozenPlayhead } from '@lib/media/useFrozenPlayhead';
import { useMediaMenuActions } from '@pages/Library/components/MediaCard/components/useMediaMenuActions';
import { MediaRenameModal } from '@pages/Library/components/MediaRenameModal/MediaRenameModal';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react';
import { MediaActionBar, type AgentScope, type MediaActionBarLayoutProps } from './MediaActionBar';

interface LibraryMediaActionBarProps
  extends Pick<SplitMediaActionsOptions, 'isTranscriptOnly' | 'canShowCta'>,
    MediaActionBarLayoutProps {
  media: Media;
  isModifiable: boolean;
  /** What a CTA click reports as. Menu rows report themselves — see `MediaActionBar.onAction`. */
  ctaControl: MediaActionControl;
  /** What a click on the unaccented pill beside the CTA reports as. */
  secondaryControl: MediaActionControl;
  /** Reads the host player's position, for the "Copy link at 1:30" row. Frozen on menu open. */
  getCurrentTime?: () => number | undefined;
  /**
   * Hands the agent verb a narrower target than the whole media — the range the viewer marked
   * on the moment modal's scrub bar. Omit it and the media-scoped behaviour stands.
   *
   * Forwarded straight to the bar, which owns the routing and the contract that comes with it
   * (the handler reports its own click — see `MediaActionBar.onWorkWithAgent`).
   */
  onWorkWithAgent?: (control: MediaActionControl) => void;
  /** The range the agent verb is narrowed to, when it is. See `MediaActionBar`. */
  agentScope?: AgentScope;
  /**
   * Rows this host contributes to the ⋯ menu and the mobile drawer, for something the action
   * hooks cannot produce. Forwarded straight to the bar; see `MediaMenuHostItem`.
   */
  hostItems?: MediaMenuHostItem[];
}

/**
 * `MediaActionBar` with the full action set — including rename, retry and delete.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MOUNT ONLY UNDER `LibraryPage`'S PROVIDERS. IT THROWS ANYWHERE ELSE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It calls `useMediaMenuActions`, which reaches `useLibraryFolder()` and `useMediaDeleteModal()`.
 * Hooks cannot be called conditionally, so a component that renders on several routes must not
 * call it — but it MAY render this behind a condition, because the condition guards a
 * *component*. That's the whole reason this exists as a separate file from the bar itself; it is
 * the same arrangement `MediaVerbRail` uses, and the constraint that shaped both.
 *
 * On `/search` and `/atlas`, render `MediaVerbsActionBar` instead. Those surfaces get everything
 * except the three library-scoped operations.
 *
 * `observer` because the gating is derived from `media.status` inside THIS component's render —
 * `useMediaMenuActions` is what reads it. Without this, a media item finishing processing would
 * leave the CTA hidden until the parent happened to re-render for some other reason.
 */
export const LibraryMediaActionBar = observer(function LibraryMediaActionBar({
  media,
  isModifiable,
  ctaControl,
  secondaryControl,
  isTranscriptOnly,
  canShowCta,
  getCurrentTime,
  onWorkWithAgent,
  agentScope,
  hostItems,
  ...layout
}: LibraryMediaActionBarProps) {
  const { playheadTime, freezePlayhead } = useFrozenPlayhead(getCurrentTime);

  const { actions, executeAction, mediaRenameModalRef, setIsRenameModalOpen } = useMediaMenuActions(
    media,
    isModifiable,
    undefined,
    playheadTime,
  );

  const model = splitMediaActions(actions, { isTranscriptOnly, canShowCta });

  return (
    <>
      <MediaActionBar
        {...layout}
        {...model}
        media={media}
        ctaControl={ctaControl}
        secondaryControl={secondaryControl}
        agentScope={agentScope}
        hostItems={hostItems}
        onWorkWithAgent={onWorkWithAgent}
        onMenuOpen={freezePlayhead}
        // `control` comes from the bar, which is the only thing that knows whether this was the
        // pill, the kebab or the drawer.
        onAction={(id: MediaMenuAction, control: MediaActionControl) => {
          void executeAction(id, control);
        }}
      />
      {/* Not mounted when this instance renders no menu. On mobile the modal splits itself
          across two instances of this component (footer pills and a header kebab), and without
          this guard the footer would mount a second, permanently inert rename modal. Harmless
          today (`useModalRegistration` no-ops while closed, and the store ref-counts) but it's a
          duplicate with real lifecycle, so don't create it. */}
      {layout.parts !== 'pills' && (
        <MediaRenameModal
          ref={mediaRenameModalRef}
          media={media}
          onOpen={() => setIsRenameModalOpen(true)}
          onClose={() => setIsRenameModalOpen(false)}
        />
      )}
    </>
  );
});
